import type { Maybe } from '@vrplatform/utils';
import { useNavigate } from 'react-router';
import { useCallback } from 'react';
import {
  parseAsString,
  parseAsArrayOf,
  parseAsStringLiteral,
  useQueryState,
} from '@vrplatform/query-params';
import { useOwnerStatementSummariesDetailContext } from './_context';
import { useDashboard } from '@vrplatform/data';

const VIEWS = ['listing', 'month', 'reservation', 'bookingChannel'] as const;

export type OwnerStatementSummariesDetailFilter = {
  monthRange: `${string}...${string}`;
  contactId: string | undefined;
  view: (typeof VIEWS)[number];
  listingIds: Maybe<string[]>;
};

export function useOwnerStatementSummariesDetailFilter() {
  'use memo';

  const [dashboard] = useDashboard();
  const isOwnerPortal = dashboard === 'owner';

  const { contactId, monthRange } = useOwnerStatementSummariesDetailContext();

  const [listingIds, setListingIds] = useQueryState(
    'listingIds',
    parseAsArrayOf(parseAsString)
  );
  const [initialView, setView] = useQueryState(
    'view',
    parseAsStringLiteral(VIEWS).withDefault('listing')
  );
  const view = initialView || 'listing';

  const goto = useNavigate();

  const setFilter = useCallback(
    (filter: {
      monthRange?: OwnerStatementSummariesDetailFilter['monthRange'];
      contactId?: OwnerStatementSummariesDetailFilter['contactId'];
    }) => {
      const params = new URLSearchParams();

      const nextMonthRange = filter.monthRange || monthRange;
      const nextContactId = filter.contactId || contactId;

      if (isOwnerPortal) {
        goto(`/statements/summary/${nextMonthRange}?${params.toString()}`);
      } else {
        if (!nextContactId) {
          goto(
            `/reports/statement-summaries/list/${nextMonthRange}?${params.toString()}`
          );
          return;
        }

        goto(
          `/reports/statement-summaries/${nextContactId}/${nextMonthRange}?${params.toString()}`
        );
      }
    },
    [contactId, goto, monthRange, isOwnerPortal]
  );

  return {
    filter: {
      monthRange,
      contactId,
      view,
      listingIds,
    } satisfies OwnerStatementSummariesDetailFilter,
    setFilter,

    setListingIds: (
      listingIds: OwnerStatementSummariesDetailFilter['listingIds']
    ) => setListingIds(listingIds?.length ? listingIds : null),
    setView: (view: OwnerStatementSummariesDetailFilter['view']) =>
      setView(view),
  };
}
